import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Rocket, Users, PlayCircle, Download, FileSpreadsheet, FileText, RefreshCw, Search, Link2, UserCheck,
  CalendarRange,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Header from "@/components/Header";
import api from "@/lib/api";
import { todayIn } from "@/lib/dates";

// Operation Momentum palette (taken from the 30-day scorecard artwork).
const OM = {
  deep: "#2E1065",
  mid: "#4C1D95",
  violet: "#7C3AED",
  glow: "#A78BFA",
  cyan: "#22D3EE",
  amber: "#FBBF24",
  lav: "#F5F3FF",
  line: "rgba(76,29,149,0.14)",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const weekLabel = (w) => {
  if (!w) return "—";
  const a = new Date(`${w}T00:00:00`);
  const b = new Date(a.getTime() + 6 * 864e5);
  const mA = MONTHS[a.getMonth()], mB = MONTHS[b.getMonth()];
  return mA === mB ? `${mA} ${a.getDate()}–${b.getDate()}` : `${mA} ${a.getDate()}–${mB} ${b.getDate()}`;
};
const monthLabel = (m) => `${MONTHS[Number(String(m).slice(5, 7)) - 1] || m} ${String(m).slice(0, 4)}`;

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const shortAddr = (a) => (a && a.length > 14 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a || "—");
const stamp = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const Kpi = ({ label, value, sub, tid, accent = OM.violet }) => (
  <div className="rounded-2xl p-4 bg-white" style={{ border: `1px solid ${OM.line}`, boxShadow: "0 1px 2px rgba(46,16,101,0.05)" }} data-testid={tid}>
    <div className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: "#8B84A8" }}>{label}</div>
    <div className="font-display font-semibold mt-1" style={{ fontSize: 26, color: accent, letterSpacing: "-0.02em" }}>{value}</div>
    {sub && <div className="text-[11px] mt-0.5" style={{ color: "#6E6889" }}>{sub}</div>}
  </div>
);

const Panel = ({ title, right, children, tid }) => (
  <div className="rounded-2xl bg-white overflow-hidden" style={{ border: `1px solid ${OM.line}` }} data-testid={tid}>
    <div className="flex items-center justify-between px-4 py-2.5" style={{ background: OM.lav, borderBottom: `1px solid ${OM.line}` }}>
      <div className="font-display text-sm font-semibold flex items-center gap-1.5" style={{ color: OM.mid }}>{title}</div>
      {right}
    </div>
    {children}
  </div>
);

const Th = ({ children, right }) => (
  <th className={`py-2 px-3 text-[10px] font-bold tracking-[0.1em] uppercase whitespace-nowrap ${right ? "text-right" : "text-left"}`} style={{ color: "#7E77A0" }}>{children}</th>
);

const BucketCompare = ({ title, rows, totals, tid, rowTid, totalLabel, cume, onCume }) => {
  const view = cume
    ? rows.reduce((acc, r) => {
        const p = acc[acc.length - 1];
        acc.push({
          ...r,
          members: (p?.members || 0) + r.members,
          with_login: (p?.with_login || 0) + r.with_login,
          mining_usd: +((p?.mining_usd || 0) + r.mining_usd).toFixed(2),
          staked_usd: +((p?.staked_usd || 0) + r.staked_usd).toFixed(2),
          total_usd: +((p?.total_usd || 0) + r.total_usd).toFixed(2),
        });
        return acc;
      }, [])
    : rows;
  return (
  <Panel title={title} tid={tid} right={
    <div className="inline-flex p-0.5 rounded-lg" style={{ background: "rgba(76,29,149,0.09)" }}>
      {[[false, "Per period", "Just this week/month on its own — a quiet period reads 0"],
        [true, "Cumulative", "Running totals — each row adds everything up to that point"]].map(([v, label, tip]) => (
        <button key={label} onClick={() => onCume(v)} title={tip}
          className="px-2.5 h-6 rounded-md text-[10px] font-bold tracking-wide uppercase transition-colors"
          style={cume === v ? { background: "white", color: OM.mid, boxShadow: "0 1px 2px rgba(76,29,149,0.18)" } : { color: "#8B84A8" }}
          data-testid={`om-cume-${v ? "on" : "off"}`}>{label}</button>
      ))}
    </div>
  }>
    <div className="grid lg:grid-cols-2">
      <div className="px-2 py-3" style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={180}>
          <BarChart data={view} barGap={2}>
            <CartesianGrid strokeDasharray="2 4" stroke="rgba(76,29,149,0.10)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8B84A8" }} />
            <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 10, fill: "#8B84A8" }} width={26} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#8B84A8" }} width={54}
              tickFormatter={(v) => `$${Number(v) >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
            <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${OM.line}`, fontSize: 12 }}
              formatter={(v, n) => (n === "New members" ? v : usd(v))} />
            <Bar yAxisId="l" dataKey="members" name="New members" fill={OM.violet} radius={[5, 5, 0, 0]} maxBarSize={30} />
            <Bar yAxisId="r" dataKey="mining_usd" name="Mining $" fill={OM.cyan} radius={[5, 5, 0, 0]} maxBarSize={30} />
            <Bar yAxisId="r" dataKey="staked_usd" name="Staking $" fill={OM.amber} radius={[5, 5, 0, 0]} maxBarSize={30} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-auto" style={{ maxHeight: 240, borderLeft: `1px solid ${OM.line}` }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10"><tr style={{ borderBottom: `1px solid ${OM.line}` }}>
            <Th>{rowTid === "om-week-row" ? "Week" : "Month"}{cume ? " (running)" : ""}</Th>
            <Th right>Members</Th><Th right>Logins</Th>
            <Th right>Mining $</Th><Th right>Staking $</Th><Th right>Total $</Th>
          </tr></thead>
          <tbody>
            {view.map((r) => (
              <tr key={r.key} className="hover:bg-[#FAF8FF]" style={{ borderBottom: "1px solid rgba(76,29,149,0.06)" }} data-testid={`${rowTid}-${r.key}`}>
                <td className="py-1.5 px-3 font-semibold whitespace-nowrap" style={{ color: OM.mid }}>{r.label}</td>
                <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold" style={{ color: r.members ? OM.violet : "#B9B3CC" }}>{r.members}</td>
                <td className="py-1.5 px-3 text-right font-mono tabular-nums text-ink-3">{r.with_login}</td>
                <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(r.mining_usd)}</td>
                <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(r.staked_usd)}</td>
                <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold">{usd(r.total_usd)}</td>
              </tr>
            ))}
            <tr style={{ background: OM.lav }} data-testid={`${rowTid}-total`}>
              <td className="py-1.5 px-3 text-[11px] font-bold uppercase tracking-wider" style={{ color: OM.mid }}>{totalLabel}</td>
              <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold">{totals?.members ?? 0}</td>
              <td className="py-1.5 px-3 text-right font-mono tabular-nums">{totals?.with_login ?? 0}</td>
              <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(totals?.mining_usd)}</td>
              <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(totals?.staked_usd)}</td>
              <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold">{usd(totals?.total_usd)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Panel>
  );
};

export default function OperationMomentum() {
  const [tab, setTab] = useState("members");
  const [mode, setMode] = useState("range"); // range | months | weeks
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(todayIn());
  const [monthYear, setMonthYear] = useState(Number(todayIn().slice(0, 4)));
  const [months, setMonths] = useState([todayIn().slice(0, 7)]);
  const [weekMonth, setWeekMonth] = useState(todayIn().slice(0, 7));
  const [cume, setCume] = useState(false);
  const [nm, setNm] = useState(null);
  const [vv, setVv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const byMonth = mode === "months" && months.length > 0;
  const byWeek = mode === "weeks";
  const memberParams = byWeek ? { months: weekMonth } : byMonth ? { months: months.join(",") } : { start, end };

  const load = async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/reports/new-members", { params: memberParams }),
        api.get("/reports/video-views", { params: { start, end } }),
      ]);
      setNm(a.data);
      setVv(b.data);
    } catch {
      toast.error("Could not load the reports");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, [start, end, mode, months.join(","), weekMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMonth = (m) => setMonths((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort()));

  const exportHref = (kind, fmt) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const t = localStorage.getItem("cp_token") || "";
    const qs = kind === "new-members" && (byMonth || byWeek)
      ? `months=${byWeek ? weekMonth : months.join(",")}&gran=${byWeek ? "week" : "month"}&cume=${cume ? 1 : 0}`
      : `start=${start}&end=${end}`;
    return `${base}/api/reports/${kind}/export?fmt=${fmt}&${qs}&token=${encodeURIComponent(t)}`;
  };

  const needle = q.trim().toLowerCase();
  const memberRows = useMemo(() => {
    const rows = nm?.rows || [];
    if (!needle) return rows;
    return rows.filter((r) => [r.name, r.email, r.wallet, r.reports_to, r.system_sponsor, r.actual_sponsor]
      .some((v) => (v || "").toLowerCase().includes(needle)));
  }, [nm, needle]);
  const memberTotals = useMemo(() => memberRows.reduce((a, r) => ({
    mining_usd: a.mining_usd + Number(r.mining_usd || 0),
    staked_ca: a.staked_ca + Number(r.staked_ca || 0),
    staked_usd: a.staked_usd + Number(r.staked_usd || 0),
    total_usd: a.total_usd + Number(r.total_usd || 0),
  }), { mining_usd: 0, staked_ca: 0, staked_usd: 0, total_usd: 0 }), [memberRows]);

  const personRows = useMemo(() => {
    const rows = vv?.persons || [];
    if (!needle) return rows;
    return rows.filter((p) => [p.name, p.email, p.phone, p.sponsor_name]
      .some((v) => (v || "").toLowerCase().includes(needle)));
  }, [vv, needle]);

  const kind = tab === "members" ? "new-members" : "video-views";  const catalog = vv?.catalog || [];
  const thisMonth = todayIn().slice(0, 7);
  const spanLabel = byWeek
    ? `${monthLabel(weekMonth)}${weekMonth === thisMonth ? " · month to date" : ""}`
    : byMonth ? months.map(monthLabel).join(" · ") : `${start} → ${end}`;
  const monthRows = (nm?.by_month || []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  const weekRows = nm?.by_week || [];

  return (
    <div className="min-h-screen pb-14" data-testid="operation-momentum-page">
      <Header />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-4">

        {/* ---------- hero ---------- */}
        <div className="relative overflow-hidden rounded-3xl px-6 md:px-9 py-7 mb-4"
          style={{ background: `linear-gradient(115deg, ${OM.deep} 0%, ${OM.mid} 52%, #6D28D9 100%)` }}
          data-testid="om-hero">
          <div className="absolute inset-0 opacity-[0.16] pointer-events-none"
            style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "42px 42px" }} />
          <div className="absolute -right-16 -top-24 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.28), transparent 65%)" }} />
          <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.16em] uppercase mb-3"
                style={{ background: "rgba(251,191,36,0.16)", color: OM.amber, border: "1px solid rgba(251,191,36,0.35)" }}>
                <Rocket size={12} /> 30-day execution reporting
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white tracking-tight uppercase" style={{ letterSpacing: "-0.02em" }}>
                Operation Momentum
              </h1>
              <p className="text-base mt-2 max-w-2xl" style={{ color: "#DCD3FF" }}>
                Who joined, who brought them in, and who is actually watching the videos — over any date range.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="inline-flex p-1 rounded-xl mr-1" style={{ background: "rgba(255,255,255,0.12)", display: tab === "members" ? undefined : "none" }}>
                {[["range", "Date range"], ["weeks", "Week by week"], ["months", "Month by month"]].map(([k, label]) => (
                  <button key={k} onClick={() => setMode(k)}
                    className="px-3 h-9 rounded-lg text-[12px] font-bold transition-colors"
                    style={mode === k ? { background: "rgba(255,255,255,0.95)", color: OM.mid } : { color: "#E7DEFF" }}
                    data-testid={`om-mode-${k}`}>{label}</button>
                ))}
              </div>
              {tab === "videos" || mode === "range" ? (
                <>
                  <div>
                    <div className="text-[10px] font-bold tracking-widest mb-1" style={{ color: "#B7A9F0" }}>FROM</div>
                    <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
                      className="h-9 px-2.5 rounded-lg text-sm bg-white/95 outline-none focus-visible:ring-2 focus-visible:ring-[#22D3EE]"
                      style={{ border: "1px solid rgba(255,255,255,0.3)", color: OM.deep }} data-testid="om-date-start" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-widest mb-1" style={{ color: "#B7A9F0" }}>TO</div>
                    <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
                      className="h-9 px-2.5 rounded-lg text-sm bg-white/95 outline-none focus-visible:ring-2 focus-visible:ring-[#22D3EE]"
                      style={{ border: "1px solid rgba(255,255,255,0.3)", color: OM.deep }} data-testid="om-date-end" />
                  </div>
                  <div className="flex items-center gap-1.5 pb-0.5">
                    {[["7d", 7], ["30d", 30], ["90d", 90], ["1y", 365]].map(([lab, d]) => (
                      <button key={lab} onClick={() => { setStart(daysAgo(d)); setEnd(todayIn()); }}
                        className="px-2.5 h-9 rounded-lg text-[11px] font-bold transition-colors"
                        style={{ background: start === daysAgo(d) ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.12)", color: start === daysAgo(d) ? OM.mid : "#E7DEFF" }}
                        data-testid={`om-range-${lab}`}>{lab}</button>
                    ))}
                    <button onClick={load} className="px-2.5 h-9 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5"
                      style={{ background: "rgba(255,255,255,0.12)", color: "#E7DEFF" }} data-testid="om-refresh">
                      <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
                    </button>
                  </div>
                </>
              ) : mode === "weeks" ? (
                <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.10)" }} data-testid="om-week-picker">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setMonthYear((y) => y - 1)} className="w-6 h-6 rounded-md text-[13px] font-bold" style={{ background: "rgba(255,255,255,0.16)", color: "white" }} data-testid="om-week-year-prev">‹</button>
                      <span className="font-display text-sm font-semibold text-white tabular-nums" data-testid="om-week-year-label">{monthYear}</span>
                      <button onClick={() => setMonthYear((y) => y + 1)} className="w-6 h-6 rounded-md text-[13px] font-bold" style={{ background: "rgba(255,255,255,0.16)", color: "white" }} data-testid="om-week-year-next">›</button>
                    </div>
                    <button onClick={() => { setMonthYear(Number(thisMonth.slice(0, 4))); setWeekMonth(thisMonth); }}
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.16)", color: "#E7DEFF" }}
                      data-testid="om-week-mtd">month to date</button>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {MONTHS.map((mLab, i) => {
                      const key = `${monthYear}-${String(i + 1).padStart(2, "0")}`;
                      return (
                        <button key={key} onClick={() => setWeekMonth(key)}
                          className="px-2 h-7 rounded-md text-[11px] font-bold transition-colors"
                          style={weekMonth === key ? { background: "white", color: OM.mid } : { background: "rgba(255,255,255,0.10)", color: "#E7DEFF" }}
                          data-testid={`om-week-month-${key}`}>{mLab}</button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl p-2.5" style={{ background: "rgba(255,255,255,0.10)" }} data-testid="om-month-picker">

                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setMonthYear((y) => y - 1)} className="w-6 h-6 rounded-md text-[13px] font-bold" style={{ background: "rgba(255,255,255,0.16)", color: "white" }} data-testid="om-year-prev">‹</button>
                      <span className="font-display text-sm font-semibold text-white tabular-nums" data-testid="om-year-label">{monthYear}</span>
                      <button onClick={() => setMonthYear((y) => y + 1)} className="w-6 h-6 rounded-md text-[13px] font-bold" style={{ background: "rgba(255,255,255,0.16)", color: "white" }} data-testid="om-year-next">›</button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold" style={{ color: "#B7A9F0" }}>{months.length} selected</span>
                      <button onClick={() => setMonths([])} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.16)", color: "#E7DEFF" }} data-testid="om-months-clear">clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {MONTHS.map((mLab, i) => {
                      const key = `${monthYear}-${String(i + 1).padStart(2, "0")}`;
                      const on = months.includes(key);
                      return (
                        <button key={key} onClick={() => toggleMonth(key)}
                          className="px-2 h-7 rounded-md text-[11px] font-bold transition-colors"
                          style={on ? { background: "white", color: OM.mid } : { background: "rgba(255,255,255,0.10)", color: "#E7DEFF" }}
                          data-testid={`om-month-${key}`}>{mLab}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ---------- tabs + tools ---------- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
          <div className="inline-flex p-1 rounded-xl" style={{ background: OM.lav, border: `1px solid ${OM.line}` }}>
            {[["members", "New members", Users], ["videos", "Video views", PlayCircle]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)}
                className="px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 transition-all"
                style={tab === k ? { background: OM.violet, color: "white", boxShadow: "0 2px 10px rgba(124,58,237,0.35)" } : { color: OM.mid }}
                data-testid={`om-tab-${k}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#9C94BC" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people, wallets, sponsors…"
                className="h-9 pl-8 pr-3 rounded-lg text-sm bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] w-64"
                style={{ border: `1px solid ${OM.line}` }} data-testid="om-search" />
            </div>
            <a href={exportHref(kind, "xlsx")} className="h-9 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 text-white"
              style={{ background: "#0F7A4A" }} data-testid="om-export-excel">
              <FileSpreadsheet size={13} /> Excel
            </a>
            <a href={exportHref(kind, "pdf")} className="h-9 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 text-white"
              style={{ background: "#B4262A" }} data-testid="om-export-pdf">
              <FileText size={13} /> PDF
            </a>
          </div>
        </div>

        {/* ---------- new members ---------- */}
        {tab === "members" && (
          <div className="space-y-3" data-testid="om-report-members">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
              <Kpi label="New members" value={nm?.totals?.members ?? "—"} sub={spanLabel} tid="om-kpi-members" />
              <Kpi label="With a login" value={nm?.totals?.with_login ?? "—"} sub="app accounts created" tid="om-kpi-logins" accent={OM.mid} />
              <Kpi label="Mining" value={usd(nm?.totals?.mining_usd)} sub="locked in USDT" tid="om-kpi-mining" accent={OM.mid} />
              <Kpi label="Staking" value={usd(nm?.totals?.staked_usd)}
                sub={`${Number(nm?.totals?.staked_ca || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} CA @ $${Number(nm?.ca_price || 0).toFixed(4)}`}
                tid="om-kpi-staked" accent="#0F7A4A" />
              <Kpi label="Total value" value={usd(nm?.totals?.total_usd)} sub="mining + staking" tid="om-kpi-total" accent={OM.violet} />
            </div>

            {(byMonth || byWeek) && (
              <BucketCompare
                title={byWeek
                  ? <><CalendarRange size={13} /> Week by week · {spanLabel}</>
                  : <><CalendarRange size={13} /> Month by month · {months.length} month{months.length === 1 ? "" : "s"} compared</>}
                rows={(byWeek ? weekRows.map((w) => ({ ...w, key: w.week })) : monthRows.map((m) => ({ ...m, key: m.month })))}
                totals={nm?.totals}
                tid={byWeek ? "om-week-compare" : "om-month-compare"}
                rowTid={byWeek ? "om-week-row" : "om-month-row"}
                totalLabel={byWeek ? monthLabel(weekMonth) : "All selected"}
                cume={cume}
                onCume={setCume}
              />
            )}

            {mode === "range" && (nm?.by_day || []).length > 0 && (
              <Panel title="Members joined per day" tid="om-joins-chart">
                <div className="px-2" style={{ height: 196, paddingTop: 12 }}>
                  <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                    <BarChart data={nm.by_day}>
                      <CartesianGrid strokeDasharray="2 4" stroke="rgba(76,29,149,0.10)" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8B84A8" }} tickFormatter={(d) => String(d).slice(5)} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8B84A8" }} width={26} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${OM.line}`, fontSize: 12 }} />
                      <Bar dataKey="members" name="Joined" fill={OM.violet} radius={[5, 5, 0, 0]} maxBarSize={26} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              {[["System sponsor", "by_system_sponsor", Link2, "om-rollup-system"], ["Actual sponsor", "by_actual_sponsor", UserCheck, "om-rollup-actual"]].map(([label, key, Icon, tid]) => (
                <Panel key={key} tid={tid}
                  title={<><Icon size={13} /> {label === "System sponsor" ? "By system sponsor (one level up)" : "By actual sponsor (defaults to system)"}</>}>
                  <div className="max-h-64 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white"><tr style={{ borderBottom: `1px solid ${OM.line}` }}>
                        <Th>{label}</Th><Th right>Members</Th><Th right>Logins</Th><Th right>Mining $</Th><Th right>Staking $</Th><Th right>Total $</Th>
                      </tr></thead>
                      <tbody>
                        {(nm?.[key] || []).map((a) => (
                          <tr key={a.name} style={{ borderBottom: "1px solid rgba(76,29,149,0.06)" }}>
                            <td className="py-1.5 px-3">{a.name}</td>
                            <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold" style={{ color: OM.violet }}>{a.members}</td>
                            <td className="py-1.5 px-3 text-right font-mono tabular-nums text-ink-3">{a.with_login}</td>
                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(a.mining_usd)}</td>
                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{usd(a.staked_usd)}</td>
                            <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold" style={{ color: OM.mid }}>{usd(a.total_usd)}</td>
                          </tr>
                        ))}
                        {!(nm?.[key] || []).length && <tr><td colSpan={6} className="py-8 text-center text-sm text-ink-3">Nothing in this range yet</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              ))}
            </div>

            <Panel title={`New member detail · ${memberRows.length} people · ${spanLabel}`} tid="om-members-table"
              right={<span className="text-[11px]" style={{ color: "#8B84A8" }}>{nm?.totals?.no_join_date ? `${nm.totals.no_join_date} wallets have no CaryPact join date` : ""}</span>}>
              <div className="overflow-auto" style={{ maxHeight: 620 }}>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10"><tr style={{ borderBottom: `1px solid ${OM.line}` }}>
                    {byWeek && <Th>Week</Th>}
                    {byMonth && <Th>Month</Th>}
                    <Th>Joined</Th><Th>Person</Th><Th>Lvl</Th><Th>Wallet</Th><Th>Email</Th>
                    <Th>System sponsor</Th><Th>Actual sponsor</Th><Th>Rank</Th>
                    <Th right>Mining $</Th><Th right>Staked CA</Th><Th right>Staking $</Th><Th right>Total $</Th>
                  </tr></thead>
                  <tbody>
                    {memberRows.map((r) => (
                      <tr key={r.node_id} className="hover:bg-[#FAF8FF]" style={{ borderBottom: "1px solid rgba(76,29,149,0.06)" }} data-testid={`om-member-row-${r.node_id}`}>
                        {byWeek && <td className="py-1.5 px-3 text-[11px] font-semibold whitespace-nowrap" style={{ color: OM.violet }}>{weekLabel(r.week)}</td>}
                        {byMonth && <td className="py-1.5 px-3 text-[11px] font-semibold whitespace-nowrap" style={{ color: OM.violet }}>{monthLabel(r.month)}</td>}
                        <td className="py-1.5 px-3 font-mono text-[11px] whitespace-nowrap" style={{ color: OM.mid }}>{r.joined}</td>
                        <td className="py-1.5 px-3 font-medium whitespace-nowrap">
                          {r.name || "—"}
                          {r.has_login && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: "rgba(15,122,74,0.12)", color: "#0F7A4A" }}>LOGIN</span>}
                        </td>
                        <td className="py-1.5 px-3 font-mono text-[11px] text-ink-3">{r.level}</td>
                        <td className="py-1.5 px-3 font-mono text-[11px] text-ink-3" title={r.wallet}>{shortAddr(r.wallet)}</td>
                        <td className="py-1.5 px-3 text-[12px] text-ink-2">{r.email || "—"}</td>
                        <td className="py-1.5 px-3 text-[12px]" title={r.system_sponsor_source === "upline" ? "One level up in the CaryPact tree" : r.system_sponsor_source ? `via ${r.system_sponsor_source}` : ""}>
                          {r.system_sponsor || <span className="text-ink-3">—</span>}
                        </td>
                        <td className="py-1.5 px-3 text-[12px]">
                          {r.actual_sponsor
                            ? (r.actual_sponsor_explicit
                              ? <span className="font-semibold" style={{ color: "#0F7A4A" }} title="Manually attributed">{r.actual_sponsor}</span>
                              : <span className="text-ink-2" title="Inherited from the system sponsor">{r.actual_sponsor}</span>)
                            : <span className="text-ink-3">—</span>}
                        </td>
                        <td className="py-1.5 px-3">
                          {r.rank ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(124,58,237,0.12)", color: OM.mid }}>{r.rank}</span> : <span className="text-ink-3">—</span>}
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px]">{usd(r.mining_usd)}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px] text-ink-3">{Number(r.staked_ca || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px]">{usd(r.staked_usd)}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px] font-bold" style={{ color: OM.mid }}>{usd(r.total_usd)}</td>
                      </tr>
                    ))}
                    {!memberRows.length && <tr><td colSpan={byMonth || byWeek ? 13 : 12} className="py-12 text-center text-sm text-ink-3">{busy ? "Loading…" : byMonth || byWeek ? "Nobody joined in the period you picked" : "No members joined in this range"}</td></tr>}
                  </tbody>
                  {memberRows.length > 0 && (
                    <tfoot className="sticky bottom-0" style={{ background: OM.lav }}>
                      <tr data-testid="om-members-total-row">
                        <td colSpan={byMonth || byWeek ? 9 : 8} className="py-2 px-3 text-[11px] font-bold uppercase tracking-wider" style={{ color: OM.mid }}>
                          Totals · {memberRows.length} people
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px] font-bold">{usd(memberTotals.mining_usd)}</td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px]">{memberTotals.staked_ca.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px] font-bold">{usd(memberTotals.staked_usd)}</td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-[12px] font-bold" style={{ color: OM.violet }}>{usd(memberTotals.total_usd)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Panel>
          </div>
        )}

        {/* ---------- video views ---------- */}
        {tab === "videos" && (
          <div className="space-y-3" data-testid="om-report-videos">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <Kpi label="People watching" value={vv?.totals?.people ?? "—"} sub={`${start} → ${end}`} tid="om-kpi-people" />
              <Kpi label="Videos completed" value={vv?.totals?.plays ?? "—"} sub="90%+ watched" tid="om-kpi-plays" accent={OM.mid} />
              <Kpi label="Sponsors with views" value={vv?.totals?.sponsors ?? "—"} sub="links generating watches" tid="om-kpi-vsponsors" accent="#0F7A4A" />
              <Kpi label="Different videos" value={vv?.totals?.videos ?? "—"} sub="across the funnel" tid="om-kpi-videos" accent={OM.violet} />
            </div>

            <Panel title="By video" tid="om-by-video">
              <div className="flex flex-wrap gap-2 p-3">
                {catalog.map((c) => {
                  const v = vv.by_video[c.slot] || {};
                  return (
                    <div key={c.slot} className="rounded-xl px-3 py-2" style={{ background: OM.lav, border: `1px solid ${OM.line}`, minWidth: 158 }} data-testid={`om-video-card-${c.slot}`}>
                      <div className="text-[11px] font-semibold" style={{ color: OM.mid }}>{c.label}</div>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="font-display font-semibold" style={{ fontSize: 20, color: OM.violet }}>{v.plays}</span>
                        <span className="text-[11px] text-ink-3">{v.unique} people</span>
                      </div>
                    </div>
                  );
                })}
                {!catalog.length && <div className="py-8 w-full text-center text-sm text-ink-3">{busy ? "Loading…" : "No video views in this range"}</div>}
              </div>
            </Panel>

            <Panel title="By sponsor · whose people are watching" tid="om-video-sponsors">
              <div className="overflow-auto" style={{ maxHeight: 320 }}>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10"><tr style={{ borderBottom: `1px solid ${OM.line}` }}>
                    <Th>Sponsor</Th><Th>Wallet name</Th><Th right>People</Th><Th right>Unique watchers</Th><Th right>Completions</Th>
                    {catalog.map((c) => <Th key={c.slot} right>{c.label.replace("Onboarding · ", "").replace("Discover · ", "")}</Th>)}
                  </tr></thead>
                  <tbody>
                    {(vv?.by_sponsor || []).map((s) => (
                      <tr key={s.sponsor_slug} className="hover:bg-[#FAF8FF]" style={{ borderBottom: "1px solid rgba(76,29,149,0.06)" }} data-testid={`om-vsponsor-row-${s.sponsor_slug}`}>
                        <td className="py-1.5 px-3 whitespace-nowrap">
                          <span className="font-medium">{s.sponsor_name || <span className="text-amber-700" title="This link code doesn't belong to any member">unknown link</span>}</span>
                          <span className="ml-1.5 font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: OM.lav, color: OM.mid, border: `1px solid ${OM.line}` }} title="The link code people arrived with">{s.sponsor_slug}</span>
                        </td>
                        <td className="py-1.5 px-3 text-[12px] whitespace-nowrap" data-testid={`om-vsponsor-wallets-${s.sponsor_slug}`}>
                          {(s.by_wallet || []).length
                            ? (s.by_wallet || []).map((w) => (
                                <span key={w.wallet_name} className="inline-block mr-1.5 text-[11px] px-1.5 py-0.5 rounded"
                                  style={{ background: OM.lav, color: OM.mid, border: `1px solid ${OM.line}` }}
                                  title="Wallet whose CaryPact link they arrived on">
                                  {w.wallet_name}{w.plays > 1 ? ` ×${w.plays}` : ""}
                                </span>
                              ))
                            : <span className="text-ink-3" title="These visits landed before per-wallet link tracking">—</span>}
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold" style={{ color: OM.violet }}>{s.people}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums">{s.unique_watchers}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums">{s.total_plays}</td>
                        {catalog.map((c) => (
                          <td key={c.slot} className="py-1.5 px-3 text-right font-mono tabular-nums text-[12px] text-ink-3">
                            {(s.videos?.[c.slot]?.plays) || <span className="opacity-30">·</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!(vv?.by_sponsor || []).length && <tr><td colSpan={5 + catalog.length} className="py-10 text-center text-sm text-ink-3">Nothing yet in this range</td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title={`Person detail · ${personRows.length} watchers`} tid="om-video-persons">
              <div className="overflow-auto" style={{ maxHeight: 620 }}>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10"><tr style={{ borderBottom: `1px solid ${OM.line}` }}>
                    <Th>Person</Th><Th>Phone</Th><Th>Email</Th><Th>Sponsor</Th><Th>Wallet name</Th><Th>Stage</Th>
                    <Th right>Videos</Th><Th right>Completions</Th><Th>First watched</Th><Th>Last watched</Th><Th>What they watched</Th>
                  </tr></thead>
                  <tbody>
                    {personRows.map((p) => (
                      <tr key={p.key} className="hover:bg-[#FAF8FF]" style={{ borderBottom: "1px solid rgba(76,29,149,0.06)" }} data-testid={`om-person-row-${p.key}`}>
                        <td className="py-1.5 px-3 font-medium whitespace-nowrap">{p.name || "—"}</td>
                        <td className="py-1.5 px-3 font-mono text-[11px] text-ink-3 whitespace-nowrap">{p.phone || "—"}</td>
                        <td className="py-1.5 px-3 text-[12px] text-ink-2">{p.email || "—"}</td>
                        <td className="py-1.5 px-3 text-[12px] whitespace-nowrap">
                          {p.sponsor_name || (p.sponsor_slug
                            ? <span className="text-amber-700" title="This link code doesn't belong to any member">unknown link</span>
                            : <span className="text-ink-3">direct</span>)}
                          {p.sponsor_slug && <span className="ml-1.5 font-mono text-[10px] text-ink-3">{p.sponsor_slug}</span>}
                        </td>
                        <td className="py-1.5 px-3 text-[12px] whitespace-nowrap" data-testid={`om-person-wallet-${p.key}`}>
                          {p.wallet_name ? (
                            <span className="inline-flex items-center gap-1">
                              {p.wallet_name}
                              {!p.wallet_proven && <span className="inline-block w-[5px] h-[5px] rounded-full bg-amber-400/90" title="Worked out from their sponsor's default share wallet, not proven by the link" />}
                            </span>
                          ) : <span className="text-ink-3" title="No link — direct visit">—</span>}
                        </td>
                        <td className="py-1.5 px-3">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={p.stage === "trainee" ? { background: "rgba(15,122,74,0.12)", color: "#0F7A4A" } : { background: "rgba(124,58,237,0.12)", color: OM.mid }}>
                            {p.stage === "trainee" ? "TRAINEE" : "PROSPECT"}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums font-bold" style={{ color: OM.violet }}>{p.unique_videos}</td>
                        <td className="py-1.5 px-3 text-right font-mono tabular-nums">{p.total_plays}</td>
                        <td className="py-1.5 px-3 font-mono text-[11px] text-ink-3 whitespace-nowrap">{stamp(p.first_watched)}</td>
                        <td className="py-1.5 px-3 font-mono text-[11px] text-ink-3 whitespace-nowrap">{stamp(p.last_watched)}</td>
                        <td className="py-1.5 px-3">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(p.videos || {}).map(([slot, n]) => {
                              const lab = (catalog.find((c) => c.slot === slot) || {}).label || slot;
                              return (
                                <span key={slot} className="text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap"
                                  style={{ background: OM.lav, color: OM.mid, border: `1px solid ${OM.line}` }}>
                                  {lab.replace("Onboarding · ", "").replace("Discover · ", "")}{n > 1 ? ` ×${n}` : ""}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!personRows.length && <tr><td colSpan={10} className="py-12 text-center text-sm text-ink-3">{busy ? "Loading…" : "No identified watchers in this range"}</td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        <div className="flex items-center gap-1.5 mt-4 text-[11px]" style={{ color: "#8B84A8" }}>
          <Download size={11} /> Excel keeps every row and every per-video column · PDF is print-ready for team meetings.
        </div>
      </div>
    </div>
  );
}
